import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { API } from "../App";
import { Sidebar } from "../components/Sidebar";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Sheet, SheetContent } from "../components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction
} from "../components/ui/alert-dialog";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import {
  Users, Search, Plus, Pencil, Trash2, Upload, Menu,
  Building2, AlertTriangle, MapPin, UsersRound
} from "lucide-react";

const ROLES = [
  { value: "collaborateur", label: "Collaborateur" },
  { value: "manager", label: "Manager" },
  { value: "directeur", label: "Directeur" },
];

const ROLE_COLORS = {
  directeur: { bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-200", avatar: "bg-purple-600" },
  manager: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", avatar: "bg-[#E63946]" },
  collaborateur: { bg: "", text: "", border: "", avatar: "bg-[#1a2634]" },
};

const PillSelect = ({ value, onChange, options, placeholder, label }) => (
  <div>
    {label && <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>}
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-9 px-3 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00A3E0]/30 focus:border-[#00A3E0]"
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((opt) => (
        <option key={typeof opt === "string" ? opt : opt.value} value={typeof opt === "string" ? opt : opt.value}>
          {typeof opt === "string" ? opt : opt.label}
        </option>
      ))}
    </select>
  </div>
);

const EmployeeFormDialog = ({ employee, concessions, onSave, onClose, open }) => {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", matricule: "",
    concession: "", role: "collaborateur",
  });

  useEffect(() => {
    if (employee) {
      setForm({
        first_name: employee.first_name || "",
        last_name: employee.last_name || "",
        email: employee.email || "",
        matricule: employee.matricule || "",
        concession: employee.concession || "",
        role: employee.role || "collaborateur",
      });
    } else {
      setForm({ first_name: "", last_name: "", email: "", matricule: "", concession: "", role: "collaborateur" });
    }
  }, [employee, open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[95vw] lg:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="emp-form-title">{employee ? "Modifier le collaborateur" : "Nouveau collaborateur"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Prenom *</label>
              <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="Jean" data-testid="emp-firstname" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Nom *</label>
              <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="DUPONT" data-testid="emp-lastname" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
            <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Genere automatiquement si vide" data-testid="emp-email" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Matricule</label>
              <Input value={form.matricule} onChange={(e) => setForm({ ...form, matricule: e.target.value })} data-testid="emp-matricule" />
            </div>
            <PillSelect value={form.concession} onChange={(v) => setForm({ ...form, concession: v })} options={concessions} placeholder="Choisir..." label="Concession *" />
          </div>
          <PillSelect value={form.role} onChange={(v) => setForm({ ...form, role: v })} options={ROLES} label="Role *" />
          {(form.role === "manager" || form.role === "directeur") && !employee && (
            <p className="text-xs text-blue-600 bg-blue-50 rounded-lg p-2.5" data-testid="team-hint">
              Vous pourrez affecter les membres de l'equipe apres la creation.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button
            onClick={() => onSave(form)}
            disabled={!form.first_name || !form.last_name || !form.concession}
            className="bg-[#00A3E0] hover:bg-[#0090c5] text-white"
            data-testid="emp-save"
          >
            {employee ? "Enregistrer" : "Creer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TeamAssignmentDialog = ({ manager, open, onClose, getAuthHeader, onSaved }) => {
  const [collaborators, setCollaborators] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && manager) {
      setLoading(true);
      setSearch("");
      axios.get(`${API}/admin/manager/${manager.id}/team`, getAuthHeader())
        .then(res => {
          setCollaborators(res.data);
          setSelected(new Set(res.data.filter(c => c.assigned).map(c => c.id)));
        })
        .catch(() => toast.error("Erreur chargement de l'equipe"))
        .finally(() => setLoading(false));
    }
  }, [open, manager]);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllFiltered = (ids) => {
    setSelected(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.add(id));
      return next;
    });
  };

  const deselectAllFiltered = (ids) => {
    setSelected(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.delete(id));
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/manager/${manager.id}/team`,
        { collaborator_ids: Array.from(selected) },
        getAuthHeader()
      );
      toast.success(`${selected.size} collaborateur(s) affecte(s)`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'affectation");
    } finally {
      setSaving(false);
    }
  };

  const filtered = collaborators.filter(c => {
    if (!search) return true;
    const term = search.toLowerCase();
    return `${c.first_name} ${c.last_name} ${c.concession} ${c.matricule}`.toLowerCase().includes(term);
  });

  const allFilteredIds = filtered.map(c => c.id);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[95vw] lg:max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" data-testid="team-dialog-title">
            <UsersRound className="h-5 w-5 text-[#00A3E0]" />
            Equipe de {manager?.first_name} {manager?.last_name}
          </DialogTitle>
          <p className="text-xs text-gray-500">
            {manager?.role === "directeur" ? "Directeur" : "Manager"} — {manager?.concession || "Multi-sites"}
          </p>
        </DialogHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-12">
            <div className="animate-spin h-6 w-6 border-2 border-[#00A3E0] border-t-transparent rounded-full" />
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-1">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <Input
                  placeholder="Rechercher un collaborateur..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 h-8 text-sm"
                  data-testid="team-search"
                />
              </div>
              <Badge className="bg-[#00A3E0] text-white border-0 shrink-0" data-testid="team-selected-count">
                {selected.size} selectionne(s)
              </Badge>
            </div>

            <div className="flex items-center gap-3 px-1 mb-1">
              <button
                className="text-xs text-[#00A3E0] hover:underline font-medium"
                onClick={() => selectAllFiltered(allFilteredIds)}
                data-testid="team-select-all"
              >
                Tout selectionner
              </button>
              <span className="text-gray-300">|</span>
              <button
                className="text-xs text-gray-500 hover:underline"
                onClick={() => deselectAllFiltered(allFilteredIds)}
                data-testid="team-deselect-all"
              >
                Tout deselectionner
              </button>
            </div>

            <ScrollArea className="flex-1 max-h-[50vh] border rounded-lg">
              <div className="divide-y divide-gray-100">
                {filtered.map(c => (
                  <label
                    key={c.id}
                    className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors ${
                      selected.has(c.id) ? "bg-blue-50/50" : ""
                    }`}
                    data-testid={`team-member-${c.id}`}
                  >
                    <Checkbox
                      checked={selected.has(c.id)}
                      onCheckedChange={() => toggle(c.id)}
                      data-testid={`team-checkbox-${c.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {c.first_name} {c.last_name}
                      </p>
                      <p className="text-[10px] text-gray-400">
                        {c.concession}{c.matricule ? ` - ${c.matricule}` : ""}
                      </p>
                    </div>
                    {c.other_manager_id && !selected.has(c.id) && (
                      <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-200 bg-amber-50 shrink-0">
                        Autre manager
                      </Badge>
                    )}
                  </label>
                ))}
                {filtered.length === 0 && (
                  <div className="flex flex-col items-center py-8 text-gray-400">
                    <Users className="h-8 w-8 mb-2" />
                    <p className="text-sm">Aucun collaborateur trouve</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </>
        )}

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#00A3E0] hover:bg-[#0090c5] text-white"
            data-testid="team-save"
          >
            {saving ? "Enregistrement..." : "Enregistrer l'equipe"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function RHAdmin() {
  const [employees, setEmployees] = useState([]);
  const [managers, setManagers] = useState([]);
  const [concessions, setConcessions] = useState([]);
  const [siteData, setSiteData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [siteFilter, setSiteFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editEmployee, setEditEmployee] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [teamManager, setTeamManager] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const fileInputRef = useRef(null);
  const { getAuthHeader } = useAuth();

  const fetchData = async () => {
    try {
      const [empRes, mgrRes, conRes] = await Promise.all([
        axios.get(`${API}/admin/employees`, getAuthHeader()),
        axios.get(`${API}/admin/managers`, getAuthHeader()),
        axios.get(`${API}/admin/concessions`, getAuthHeader()),
      ]);
      setEmployees(empRes.data);
      setManagers(mgrRes.data);
      setConcessions(conRes.data);
    } catch (e) {
      toast.error("Erreur chargement");
    } finally {
      setLoading(false);
    }
  };

  const fetchSiteData = async (concession) => {
    try {
      const url = concession ? `${API}/admin/dashboard-by-site?concession=${encodeURIComponent(concession)}` : `${API}/admin/dashboard-by-site`;
      const res = await axios.get(url, getAuthHeader());
      setSiteData(res.data);
    } catch (e) {
      toast.error("Erreur chargement donnees site");
    }
  };

  useEffect(() => { fetchData(); fetchSiteData(); }, []);

  const handleSave = async (form) => {
    try {
      if (editEmployee) {
        await axios.put(`${API}/admin/employees/${editEmployee.id}`, form, getAuthHeader());
        toast.success("Collaborateur modifie");
        setFormOpen(false);
        setEditEmployee(null);
        await fetchData();
        if (form.role === "manager" || form.role === "directeur") {
          setTeamManager({ ...editEmployee, ...form });
        }
      } else {
        const res = await axios.post(`${API}/admin/employees`, form, getAuthHeader());
        toast.success(`Cree — MDP temporaire : ${res.data.temp_password}`);
        setFormOpen(false);
        setEditEmployee(null);
        await fetchData();
        if (form.role === "manager" || form.role === "directeur") {
          setTeamManager({ id: res.data.id, ...form });
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await axios.delete(`${API}/admin/employees/${id}`, getAuthHeader());
      const orphaned = res.data.orphaned || 0;
      if (orphaned > 0) {
        toast.success(`Supprime — ${orphaned} collaborateur(s) sont maintenant sans manager`);
      } else {
        toast.success("Collaborateur supprime");
      }
      setDeleteConfirm(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImportLoading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/admin/import-employees`, formData, {
        headers: { ...getAuthHeader().headers, "Content-Type": "multipart/form-data" },
      });
      toast.success(`Import: ${res.data.created} crees, ${res.data.updated} mis a jour`);
      if (res.data.excel_base64) {
        const byteStr = atob(res.data.excel_base64);
        const bytes = new Uint8Array(byteStr.length);
        for (let i = 0; i < byteStr.length; i++) bytes[i] = byteStr.charCodeAt(i);
        const blob = new Blob([bytes], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = res.data.filename;
        a.click();
      }
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur import");
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const orphanCount = useMemo(() => {
    if (!deleteConfirm) return 0;
    if (deleteConfirm.role !== "manager" && deleteConfirm.role !== "directeur") return 0;
    return employees.filter(e => e.manager_id === deleteConfirm.id).length;
  }, [deleteConfirm, employees]);

  const filtered = useMemo(() => {
    return employees.filter((e) => {
      if (e.role === "rh") return false;
      const term = search.toLowerCase();
      const matchSearch = !term || `${e.first_name} ${e.last_name} ${e.email} ${e.matricule}`.toLowerCase().includes(term);
      const matchSite = !siteFilter || e.concession === siteFilter;
      const matchRole = !roleFilter || e.role === roleFilter;
      return matchSearch && matchSite && matchRole;
    });
  }, [employees, search, siteFilter, roleFilter]);

  const siteFiltered = useMemo(() => {
    if (!siteFilter) return siteData;
    return siteData.filter((e) => e.concession === siteFilter);
  }, [siteData, siteFilter]);

  const siteStats = useMemo(() => {
    const data = siteFiltered;
    if (!data.length) return null;
    const totalRemaining = data.reduce((s, e) => s + (e.cp_total_remaining || 0), 0);
    const avgRemaining = totalRemaining / data.length;
    const lowCP = data.filter((e) => (e.cp_total_remaining || 0) <= 5).length;
    const highCP = data.filter((e) => (e.cp_total_remaining || 0) > 20).length;
    return { total: data.length, avgRemaining: avgRemaining.toFixed(1), lowCP, highCP };
  }, [siteFiltered]);

  const getManagerTeamCount = (empId) => {
    const mgr = managers.find(m => m.id === empId);
    return mgr?.team_count || 0;
  };

  if (loading) return (
    <div className="flex min-h-screen">
      <div className="hidden lg:block"><Sidebar /></div>
      <main className="flex-1 bg-gray-50 p-4 lg:p-8">
        <div className="animate-pulse space-y-4"><div className="h-8 bg-gray-200 rounded w-1/3" /><div className="h-64 bg-gray-200 rounded-lg" /></div>
      </main>
    </div>
  );

  return (
    <div className="flex min-h-screen">
      <div className="hidden lg:block"><Sidebar /></div>
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="p-0 w-64"><Sidebar /></SheetContent>
      </Sheet>

      <main className="flex-1 bg-gray-50">
        <header className="bg-white border-b border-gray-100 px-4 lg:px-8 py-4 lg:py-6 sticky top-0 z-40">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileMenuOpen(true)}>
                <Menu className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-lg lg:text-2xl font-bold text-[#1a2634]" style={{ fontFamily: "Chivo, sans-serif" }}>Administration</h1>
                <p className="text-gray-500 text-sm hidden sm:block">Gestion des collaborateurs et sites</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-[#1a2634] text-white border-0">{employees.filter(e => e.role !== "rh").length} collaborateurs</Badge>
              <Badge variant="outline" className="hidden sm:flex">{concessions.length} sites</Badge>
            </div>
          </div>
        </header>

        <div className="p-4 lg:p-8">
          <Tabs defaultValue="employees" className="space-y-4 lg:space-y-6" onValueChange={(v) => { if (v === "sites") fetchSiteData(siteFilter); }}>
            <TabsList className="bg-white border shadow-sm w-full lg:w-auto">
              <TabsTrigger value="employees" className="data-[state=active]:bg-gray-100 text-xs lg:text-sm" data-testid="tab-employees">
                <Users className="h-3.5 w-3.5 lg:h-4 lg:w-4 mr-1 lg:mr-2" />
                Collaborateurs
              </TabsTrigger>
              <TabsTrigger value="sites" className="data-[state=active]:bg-gray-100 text-xs lg:text-sm" data-testid="tab-sites">
                <Building2 className="h-3.5 w-3.5 lg:h-4 lg:w-4 mr-1 lg:mr-2" />
                Sites
              </TabsTrigger>
            </TabsList>

            {/* ==================== EMPLOYEES TAB ==================== */}
            <TabsContent value="employees" className="space-y-4">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 lg:gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Rechercher par nom, email, matricule..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 h-9"
                    data-testid="emp-search"
                  />
                </div>
                <div className="flex gap-2">
                  <select
                    value={siteFilter}
                    onChange={(e) => setSiteFilter(e.target.value)}
                    className="h-9 px-2 rounded-lg border border-gray-200 bg-white text-xs lg:text-sm min-w-[120px]"
                    data-testid="emp-site-filter"
                  >
                    <option value="">Tous les sites</option>
                    {concessions.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    className="h-9 px-2 rounded-lg border border-gray-200 bg-white text-xs lg:text-sm"
                    data-testid="emp-role-filter"
                  >
                    <option value="">Tous roles</option>
                    <option value="directeur">Directeurs</option>
                    <option value="manager">Managers</option>
                    <option value="collaborateur">Collaborateurs</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="bg-[#00A3E0] hover:bg-[#0090c5] text-white" onClick={() => { setEditEmployee(null); setFormOpen(true); }} data-testid="emp-add">
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    <span className="hidden sm:inline">Nouveau</span>
                  </Button>
                  <input type="file" accept=".xlsx,.xls" ref={fileInputRef} onChange={handleImport} className="hidden" />
                  <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={importLoading} data-testid="emp-import">
                    <Upload className="h-3.5 w-3.5 mr-1" />
                    <span className="hidden sm:inline">{importLoading ? "Import..." : "Import Excel"}</span>
                  </Button>
                </div>
              </div>

              <Card className="border-0 shadow-sm">
                <ScrollArea className="h-[calc(100vh-280px)]">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="text-left p-3 font-medium text-gray-500 text-xs">Collaborateur</th>
                          <th className="text-left p-3 font-medium text-gray-500 text-xs hidden lg:table-cell">Matricule</th>
                          <th className="text-left p-3 font-medium text-gray-500 text-xs">Site</th>
                          <th className="text-left p-3 font-medium text-gray-500 text-xs hidden sm:table-cell">Role</th>
                          <th className="text-left p-3 font-medium text-gray-500 text-xs hidden lg:table-cell">Manager / Equipe</th>
                          <th className="text-right p-3 font-medium text-gray-500 text-xs w-28">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {filtered.map((emp) => {
                          const mgr = managers.find((m) => m.id === emp.manager_id);
                          const colors = ROLE_COLORS[emp.role] || ROLE_COLORS.collaborateur;
                          const isLeader = emp.role === "manager" || emp.role === "directeur";
                          const teamCount = isLeader ? getManagerTeamCount(emp.id) : 0;

                          return (
                            <tr key={emp.id} className="hover:bg-gray-50/50 transition-colors" data-testid={`emp-row-${emp.id}`}>
                              <td className="p-3">
                                <div className="flex items-center gap-2.5">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 ${colors.avatar}`}>
                                    {`${emp.first_name?.[0] || ""}${emp.last_name?.[0] || ""}`}
                                  </div>
                                  <div className="min-w-0">
                                    <p className="font-medium text-gray-900 text-sm truncate">{emp.first_name} {emp.last_name}</p>
                                    <p className="text-[10px] text-gray-400 truncate">{emp.email}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="p-3 text-gray-500 text-xs hidden lg:table-cell">{emp.matricule || "—"}</td>
                              <td className="p-3">
                                <span className="text-xs text-gray-700 truncate block max-w-[120px] lg:max-w-none">{emp.concession || "—"}</span>
                              </td>
                              <td className="p-3 hidden sm:table-cell">
                                {isLeader ? (
                                  <Badge className={`${colors.bg} ${colors.text} ${colors.border} text-[10px]`}>
                                    {emp.role === "directeur" ? "Directeur" : "Manager"}
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="text-[10px]">Collaborateur</Badge>
                                )}
                              </td>
                              <td className="p-3 text-xs text-gray-500 hidden lg:table-cell">
                                {isLeader ? (
                                  <span className="text-[11px]">
                                    <UsersRound className="h-3 w-3 inline mr-1 text-gray-400" />
                                    {teamCount} membre{teamCount !== 1 ? "s" : ""}
                                  </span>
                                ) : (
                                  mgr ? `${mgr.first_name} ${mgr.last_name}` : <span className="text-gray-300">—</span>
                                )}
                              </td>
                              <td className="p-3 text-right">
                                <div className="flex justify-end gap-1">
                                  {isLeader && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7 hover:text-[#00A3E0]"
                                      onClick={() => setTeamManager(emp)}
                                      title="Gerer l'equipe"
                                      data-testid={`emp-team-${emp.id}`}
                                    >
                                      <UsersRound className="h-3.5 w-3.5 text-gray-400" />
                                    </Button>
                                  )}
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditEmployee(emp); setFormOpen(true); }} data-testid={`emp-edit-${emp.id}`}>
                                    <Pencil className="h-3 w-3 text-gray-400" />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-7 w-7 hover:text-red-500" onClick={() => setDeleteConfirm(emp)} data-testid={`emp-delete-${emp.id}`}>
                                    <Trash2 className="h-3 w-3 text-gray-400" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {filtered.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                      <Users className="h-10 w-10 mb-3" />
                      <p className="text-sm">Aucun collaborateur trouve</p>
                    </div>
                  )}
                </ScrollArea>
                <div className="px-4 py-2 border-t border-gray-100 bg-gray-50">
                  <p className="text-xs text-gray-400">{filtered.length} resultat(s)</p>
                </div>
              </Card>
            </TabsContent>

            {/* ==================== SITES TAB ==================== */}
            <TabsContent value="sites" className="space-y-4">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <select
                  value={siteFilter}
                  onChange={(e) => { setSiteFilter(e.target.value); fetchSiteData(e.target.value); }}
                  className="h-9 px-3 rounded-lg border border-gray-200 bg-white text-sm flex-1 sm:max-w-xs"
                  data-testid="site-filter"
                >
                  <option value="">Tous les sites</option>
                  {concessions.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                {siteStats && (
                  <div className="flex gap-2 flex-wrap">
                    <Badge className="bg-[#1a2634] text-white border-0">{siteStats.total} collab.</Badge>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Moy. {siteStats.avgRemaining}j restants</Badge>
                    {siteStats.lowCP > 0 && <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">{siteStats.lowCP} solde bas</Badge>}
                    {siteStats.highCP > 0 && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">{siteStats.highCP} &gt;20j restants</Badge>}
                  </div>
                )}
              </div>

              <Card className="border-0 shadow-sm">
                <ScrollArea className="h-[calc(100vh-280px)]">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="text-left p-3 font-medium text-gray-500 text-xs">Collaborateur</th>
                          <th className="text-left p-3 font-medium text-gray-500 text-xs hidden sm:table-cell">Site</th>
                          <th className="text-right p-3 font-medium text-gray-500 text-xs">CP N-2</th>
                          <th className="text-right p-3 font-medium text-gray-500 text-xs">CP N-1</th>
                          <th className="text-right p-3 font-medium text-gray-500 text-xs hidden sm:table-cell">Anc.</th>
                          <th className="text-right p-3 font-medium text-gray-500 text-xs">Total</th>
                          <th className="text-right p-3 font-medium text-gray-500 text-xs hidden lg:table-cell">En cours</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {siteFiltered.map((emp) => {
                          const total = emp.cp_total_remaining || 0;
                          const isLow = total <= 5;
                          const isHigh = total > 20;
                          const colors = ROLE_COLORS[emp.role] || ROLE_COLORS.collaborateur;
                          return (
                            <tr key={emp.id} className="hover:bg-gray-50/50 transition-colors" data-testid={`site-row-${emp.id}`}>
                              <td className="p-3">
                                <div className="flex items-center gap-2">
                                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0 ${colors.avatar}`}>
                                    {`${emp.first_name?.[0] || ""}${emp.last_name?.[0] || ""}`}
                                  </div>
                                  <div className="min-w-0">
                                    <p className="font-medium text-gray-900 text-xs truncate">{emp.first_name} {emp.last_name}</p>
                                    <p className="text-[10px] text-gray-400">{emp.matricule || ""}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="p-3 text-xs text-gray-500 hidden sm:table-cell truncate max-w-[150px]">{emp.concession}</td>
                              <td className="p-3 text-right text-xs">{emp.cp_n2_remaining > 0 ? <span className="text-blue-600 font-medium">{emp.cp_n2_remaining}j</span> : <span className="text-gray-300">0</span>}</td>
                              <td className="p-3 text-right text-xs">{emp.cp_n1_remaining > 0 ? <span className="text-[#00A3E0] font-medium">{emp.cp_n1_remaining}j</span> : <span className="text-gray-300">0</span>}</td>
                              <td className="p-3 text-right text-xs hidden sm:table-cell">{emp.cp_anc_remaining > 0 ? <span className="text-purple-600 font-medium">{emp.cp_anc_remaining}j</span> : <span className="text-gray-300">0</span>}</td>
                              <td className="p-3 text-right">
                                <span className={`text-xs font-bold ${isLow ? "text-red-500" : isHigh ? "text-amber-600" : "text-emerald-600"}`}>
                                  {total}j
                                </span>
                              </td>
                              <td className="p-3 text-right text-xs text-gray-400 hidden lg:table-cell">{emp.cp_en_cours > 0 ? `+${emp.cp_en_cours}j` : "—"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {siteFiltered.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                      <MapPin className="h-10 w-10 mb-3" />
                      <p className="text-sm">Selectionnez un site</p>
                    </div>
                  )}
                </ScrollArea>
                <div className="px-4 py-2 border-t border-gray-100 bg-gray-50">
                  <p className="text-xs text-gray-400">{siteFiltered.length} collaborateur(s)</p>
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Employee form dialog */}
        <EmployeeFormDialog
          employee={editEmployee}
          concessions={concessions}
          onSave={handleSave}
          onClose={() => { setFormOpen(false); setEditEmployee(null); }}
          open={formOpen}
        />

        {/* Team assignment dialog */}
        <TeamAssignmentDialog
          manager={teamManager}
          open={!!teamManager}
          onClose={() => setTeamManager(null)}
          getAuthHeader={getAuthHeader}
          onSaved={fetchData}
        />

        {/* Delete confirm dialog with orphan warning */}
        <AlertDialog open={!!deleteConfirm} onOpenChange={(o) => !o && setDeleteConfirm(null)}>
          <AlertDialogContent className="max-w-sm">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                Confirmer la suppression
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3">
                  {deleteConfirm && (
                    <p className="text-sm text-gray-600">
                      Supprimer <strong>{deleteConfirm.first_name} {deleteConfirm.last_name}</strong> ? Cette action est irreversible.
                    </p>
                  )}
                  {orphanCount > 0 && (
                    <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="orphan-warning">
                      <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-amber-800">
                          Attention : {orphanCount} collaborateur{orphanCount > 1 ? "s" : ""} sans manager
                        </p>
                        <p className="text-xs text-amber-600 mt-0.5">
                          {orphanCount > 1
                            ? "Ces collaborateurs se retrouveront sans responsable affecte."
                            : "Ce collaborateur se retrouvera sans responsable affecte."
                          }
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-delete">Annuler</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-500 hover:bg-red-600 text-white"
                onClick={() => handleDelete(deleteConfirm.id)}
                data-testid="confirm-delete"
              >
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </main>
    </div>
  );
}
